import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { WarningIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { EmbeddedMedia } from '#src/app/shared/components/embeddedMedia';

import type { MediaHitEmbed } from '../../../shared/context';
import { useMediaHitsPermissions } from '../../../shared/hooks/useMediaHitsPermissions';
import { DeleteMediaHit } from '../../DeleteMediaHit';
import { LoadingSkeleton } from '../../LoadingSkeleton';

const CARD_CLASS_NAME = 'w-115 shrink-0 snap-start scroll-ml-6 md:snap-align-none md:scroll-ml-0';

type DeleteButtonPosition = 'inline' | 'overlay';

type MediaHitProps = {
	media: MediaHitEmbed;
	removeMedia: (mediaId: string) => void;
	deleteButtonPosition?: DeleteButtonPosition;
};

const DeleteButtonSlot: React.FC<{
	media: MediaHitEmbed;
	removeMedia: (mediaId: string) => void;
	position: DeleteButtonPosition;
}> = ({ media, removeMedia, position }) =>
	position === 'overlay' ? (
		<div className="absolute right-1 bottom-2 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:opacity-100">
			<DeleteMediaHit media={media} removeMedia={removeMedia} />
		</div>
	) : (
		<div className="my-2">
			<DeleteMediaHit media={media} removeMedia={removeMedia} />
		</div>
	);

const InvalidMediaHitContent: React.FC<MediaHitProps> = ({ media, removeMedia, deleteButtonPosition = 'inline' }) => {
	const { translate } = useI18n();

	return (
		<>
			<div
				className={cn(
					'flex h-27.5 rounded-lg bg-[#403a490d] p-4 text-black',
					deleteButtonPosition === 'overlay' ? 'w-full' : 'w-115',
				)}
			>
				<WarningIcon size="small" className="text-icon-warning mt-0.5 mr-2 shrink-0" />
				<div className="flex h-full min-w-0 flex-1 flex-col justify-between overflow-hidden">
					<Text variant="heading" size="xx-small" truncate>
						{translate('corgi.petition_gamma.media_mentions.invalid.title')}
					</Text>
					<Text as="p" size="small" truncate>
						<Translate value="corgi.petition_gamma.media_mentions.invalid.message" />
					</Text>
					<Text as="p" size="small" className="line-clamp-2 text-wrap">
						URL: {media.url}
					</Text>
				</div>
			</div>
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<DeleteButtonSlot media={media} removeMedia={removeMedia} position={deleteButtonPosition} />
		</>
	);
};

const ValidMediaHitContent: React.FC<MediaHitProps> = ({ media, removeMedia, deleteButtonPosition = 'inline' }) => {
	return (
		<div>
			<EmbeddedMedia html={media.html} aspectRatio={16 / 9} />
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<DeleteButtonSlot media={media} removeMedia={removeMedia} position={deleteButtonPosition} />
		</div>
	);
};

export const MediaHitContent: React.FC<MediaHitProps> = ({ media, removeMedia, deleteButtonPosition = 'inline' }) => (
	<>
		{media.status === 'loading' && <LoadingSkeleton />}
		{media.status === 'valid' && (
			<ValidMediaHitContent media={media} removeMedia={removeMedia} deleteButtonPosition={deleteButtonPosition} />
		)}
		{media.status === 'invalid' && (
			<InvalidMediaHitContent media={media} removeMedia={removeMedia} deleteButtonPosition={deleteButtonPosition} />
		)}
	</>
);

export const MediaHit: React.FC<Omit<MediaHitProps, 'deleteButtonPosition'>> = ({ media, removeMedia }) => {
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	// Only show invalid media if the user can delete it. Once an embed has resolved invalid and the user can't
	// delete it, the card collapses out of the carousel entirely.
	if (media.status === 'invalid' && !canDelete) {
		return null;
	}

	return (
		<div data-testid={`media-hit-${media.id}`} className={CARD_CLASS_NAME}>
			<MediaHitContent media={media} removeMedia={removeMedia} />
		</div>
	);
};
